import { useState, useEffect, useRef, useCallback } from "react";

/* ─── Radiant spectrum — brilliant iridescent palette ─── */
const GOLD = "#A855F7";
const GOLD_LIGHT = "#C084FC";
const GOLD_DARK = "#6D28D9";
const GOLD_CREAM = "#F0E6FF";
const GOLD_GRADIENT = "linear-gradient(135deg, #F43F5E 0%, #EC4899 15%, #A855F7 30%, #6366F1 50%, #3B82F6 65%, #06B6D4 80%, #10B981 100%)";
const GOLD_GRADIENT_H = "linear-gradient(90deg, #F43F5E 0%, #EC4899 15%, #A855F7 30%, #6366F1 50%, #3B82F6 65%, #06B6D4 80%, #10B981 100%)";
const GOLD_GLOW = "rgba(168, 85, 247, 0.35)";

const themes = {
  dark: {
    bg: "#0a0a0a", bgAlt: "#0e0e0e", bgCard: "#111", text: "#f0ece4",
    textSec: "#a09888", textMuted: "#5a5248", border: "#1e1c18", borderStrong: "#33302a",
    headerBg: "rgba(10,10,10,0.92)", headerSolid: "rgba(10,10,10,0.98)",
    inputBg: "#111", contactBg: "#0e0c08",
  },
  light: {
    bg: "#f5f0e8", bgAlt: "#ece5d8", bgCard: "#fff", text: "#1a1510",
    textSec: "#5a5040", textMuted: "#9a9080", border: "#d8d0c0", borderStrong: "#b0a890",
    headerBg: "rgba(245,240,232,0.92)", headerSolid: "rgba(245,240,232,0.98)",
    inputBg: "#fff", contactBg: "#ece5d8",
  },
};

const i18n = {
  en: {
    badge: "Agentic Creative Studio",
    heroTitle1: "We design",
    heroTitle2: "the future",
    heroTitle3: "autonomously.",
    heroDesc: "Human intuition bonded with machine intelligence. Autonomous creative workflows that ship at a different speed.",
    ctaPrimary: "Start a Project",
    ctaSecondary: "How we work",
    statsLabel: "By the Numbers",
    stats: [
      { value: "97%", label: "Faster delivery" },
      { value: "4.2×", label: "Output multiplier" },
      { value: "∞", label: "Scale capacity" },
      { value: "<48h", label: "First concepts" },
    ],
    partnersLabel: "Sharing",
    partnersTitle: "Stability",
    skillsLabel: "Skills",
    skillsTitle: "Capabilities that compound.",
    skills: [
      { num: "01", title: "Brand & Identity", desc: "Visual systems built from first principles. Fundamental, irreducible, unmistakably yours." },
      { num: "02", title: "Campaign Production", desc: "One brief, exponential output. Every medium, every platform. Days, not months." },
      { num: "03", title: "Launch Materials", desc: "Pitch decks, sites, assets. Engineered to break through. Investor-ready on arrival." },
      { num: "04", title: "Autonomous Workflows", desc: "Creative systems without friction. Scale output without scaling headcount." },
      { num: "05", title: "Content & Signal", desc: "Your voice amplified across every channel. Consistent frequency, zero distortion." },
      { num: "06", title: "Data & Narrative", desc: "Invisible patterns made visible. Complex data into stories that move people." },
    ],
    naturalLabel: "Natural",
    naturalTitle: "This is the way.",
    steps: [
      { num: "01", label: "Observe", desc: "Deep immersion" },
      { num: "02", label: "Model", desc: "Structure insight" },
      { num: "03", label: "Synthesize", desc: "Generate solutions" },
      { num: "04", label: "Refine", desc: "Iterate precision" },
      { num: "05", label: "Deploy", desc: "Launch & measure" },
    ],
    intLabel: "Integrations",
    intTitle: "Your elements, our system.",
    contactLabel: "Contact",
    contactTitle: "Let's bond.",
    contactDesc: "Start a conversation by providing your email.",
    submit: "Submit",
    navItems: ["Sharing", "Skills", "Natural", "Bonding"],
    navCta: "Get Started",
    footerLinks: ["Sharing", "Skills", "Natural", "Bonding"],
    footerTag: "© 2026 -1",
  },
  ja: {
    badge: "エージェンティック クリエイティブ スタジオ", 
    heroTitle1: "未来を",
    heroTitle2: "自律的に",
    heroTitle3: "デザインする", 
    heroDesc: "人間の直感と機械の知能が融合した。自律的なクリエイティブワークフローを記録的な速さで実現",
    ctaPrimary: "プロジェクトを始める",
    ctaSecondary: "私たちの働き方",
    statsLabel: "数字で見る",
    stats: [
      { value: "97%", label: "納品速度向上"},
      { value: "4.2×", label: "アウトプット倍率"},
      { value: "∞", label: "スケール容量"},
      { value: "<48h", label: "初回コンセプト"},
    ],
    partnersLabel: "ボンディング",
    partnersTitle: "安定。",
    skillsLabel: "能力",
    skillsTitle: "私たちの仕事。",
    skills: [
      { num: "01", title: "ブランド＆アイデンティティ", desc: "第一原理から構築。根本的で、還元不可能。紛れもなくあなたのもの。" },
      { num: "02", title: "キャンペーン制作", desc: "一つのブリーフ、指数関数的出力。全メディア、全プラットフォーム。" },
      { num: "03", title: "ローンチ素材", desc: "ピッチデッキ、サイト、アセット。突破力。投資家対応済み。" },
      { num: "04", title: "自律型ワークフロー", desc: "摩擦のないクリエイティブシステム。人員増なしで拡大。" },
      { num: "05", title: "コンテンツ＆シグナル", desc: "全チャネルで増幅される声。一貫した周波数、歪みゼロ。" },
      { num: "06", title: "データ＆ナラティブ", desc: "見えないパターンの可視化。データを動かすストーリーへ。" },
    ],
    naturalLabel: "自然",
    naturalTitle: "これが正しい道だ。",
    steps: [
      { num: "01", label: "観察", desc: "深い没入" },
      { num: "02", label: "モデル", desc: "構造化" },
      { num: "03", label: "合成", desc: "解決策生成" },
      { num: "04", label: "精製", desc: "精密な反復" },
      { num: "05", label: "展開", desc: "実行と計測" },
    ],
    intLabel: "統合ツール",
    intTitle: "あなたのツール。私たちのシステム。",
    contactLabel: "お問い合わせ",
    contactTitle: "結合しましょう。",
    contactDesc: "会話を始めましょう。何が生まれるか確かめましょう。",
    submit: "送信",
    navItems: ["共有", "スキル", "自然", "結合"],
    navCta: "始める",
    footerLinks: ["実績", "サービス", "会社概要", "ブログ"],
    footerTag: "© 2026 -1",
  },
};

const partnersList = [
 "Adobe", "Discord", "Figma", "Github", "Google"
];

const partnerIcons = {
  Adobe: (
    <svg viewBox="95 110 325 292" height="56" fill="currentColor">
      <path d="M296,120h114v272zm-80,0h-114v272zm39,99-48,118h52l23,55h46z"/>
    </svg>
  ),
  Discord: (
    <svg viewBox="0 2 24 20" height="56" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  ),
  Figma: (
    <svg viewBox="0 0 200 300" height="56" fill="currentColor">
      <path d="M50 300c27.6 0 50-22.4 50-50v-50H50c-27.6 0-50 22.4-50 50s22.4 50 50 50zM0 150c0-27.6 22.4-50 50-50h50v100H50c-27.6 0-50-22.4-50-50zM0 50C0 22.4 22.4 0 50 0h50v100H50C22.4 100 0 77.6 0 50zM100 0h50c27.6 0 50 22.4 50 50s-22.4 50-50 50h-50V0zM200 150c0 27.6-22.4 50-50 50s-50-22.4-50-50 22.4-50 50-50 50 22.4 50 50z"/>
    </svg>
  ),
  Github: (
    <svg viewBox="0 0 24 24" height="56" fill="currentColor">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
    </svg>
  ),
  Google: (
    <svg viewBox="0 0 24 24" height="56" fill="currentColor">
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.8 4.133-1.147 1.147-2.933 2.4-6.04 2.4-4.813 0-8.573-3.88-8.573-8.693s3.76-8.693 8.573-8.693c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
    </svg>
  ),
};

/* ─── Toast notification ─── */
function useToast() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const show = useCallback((message, type = "success") => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ message, type, key: Date.now() });
    timerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return { toast, show };
}

function Toast({ toast, theme }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!toast) { setVisible(false); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    const t = setTimeout(() => setVisible(false), 3500);
    return () => clearTimeout(t);
  }, [toast]);
  if (!toast) return null;
  const isSuccess = toast.type === "success";
  return (
    <div key={toast.key} style={{
      position: "fixed", top: 24, right: 24, zIndex: 10000,
      background: theme.bgCard,
      border: `1px solid ${isSuccess ? "#10B981" : "#F43F5E"}`,
      borderRadius: 10, padding: "14px 22px",
      display: "flex", alignItems: "center", gap: 10,
      boxShadow: `0 8px 32px rgba(0,0,0,0.25), 0 0 0 1px ${isSuccess ? "rgba(16,185,129,0.15)" : "rgba(244,63,94,0.15)"}`,
      transform: visible ? "translateX(0)" : "translateX(calc(100% + 40px))",
      opacity: visible ? 1 : 0,
      transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1), opacity 0.4s cubic-bezier(0.16,1,0.3,1)",
      pointerEvents: visible ? "auto" : "none",
      maxWidth: 360,
    }}>
      <span style={{
        width: 22, height: 22, borderRadius: "50%",
        background: isSuccess ? "#10B981" : "#F43F5E",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: "0.75rem", color: "#fff", fontWeight: 700,
      }}>
        {isSuccess ? "\u2713" : "!"}
      </span>
      <span style={{
        color: theme.text, fontSize: "0.82rem", fontWeight: 500,
        letterSpacing: "0.01em", lineHeight: 1.4,
      }}>{toast.message}</span>
    </div>
  );
}

/* ─── hooks ─── */
function useParallax() {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => { setScrollY(window.scrollY); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrollY;
}

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {}, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={className} style={{
      ...style, opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

/* Gold text with metallic gradient */
function GoldText({ children, style = {}, as = "span" }) {
  const Tag = as;
  return (
    <Tag style={{
      background: GOLD_GRADIENT, WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent", backgroundClip: "text",
      ...style,
    }}>{children}</Tag>
  );
}

/* ─── Agentic Network Visualization (canvas) ─── */
function ParametricRibbon({ scrollY, dark }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const scrollRef = useRef(0);
  const timeRef = useRef(0);
  const nodesRef = useRef(null);
  const pulsesRef = useRef([]);
  const darkRef = useRef(dark);

  scrollRef.current = scrollY;
  darkRef.current = dark;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let running = true;
    let W, H, dpr;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // ── Initialize agent nodes ──
    // Central hub + orbital agents + peripheral creative sparks
    const createNodes = () => {
      const nodes = [];
      const cx = 0.5, cy = 0.5;

      // Central orchestrator — the "negative1 brain"
      nodes.push({
        x: cx, y: cy, baseX: cx, baseY: cy,
        r: 14, type: "core", label: "",
        orbitSpeed: 0, orbitRadius: 0, orbitPhase: 0,
        pulsePhase: 0, connections: [],
      });

      // Inner ring — primary agents (6)
      const agentLabels = ["GEN", "CUR", "REF", "SYN", "DPL", "OBS"];
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const rad = 0.18;
        nodes.push({
          x: cx + Math.cos(angle) * rad,
          y: cy + Math.sin(angle) * rad,
          baseX: cx + Math.cos(angle) * rad,
          baseY: cy + Math.sin(angle) * rad,
          r: 6, type: "agent", label: agentLabels[i],
          orbitSpeed: 0.08 + i * 0.012,
          orbitRadius: rad,
          orbitPhase: angle,
          pulsePhase: i * 1.05,
          connections: [0], // connected to core
        });
      }

      // Outer ring — output/capability nodes (10)
      const capLabels = ["Brand", "Campaign", "Launch", "Auto", "Content",
        "Data", "Visual", "Copy", "Motion", "Strategy"];
      for (let i = 0; i < 10; i++) {
        const angle = (i / 10) * Math.PI * 2 + 0.3;
        const rad = 0.34 + Math.sin(i * 1.2) * 0.04;
        const parentAgent = 1 + (i % 6); // connect to nearest inner agent
        nodes.push({
          x: cx + Math.cos(angle) * rad,
          y: cy + Math.sin(angle) * rad,
          baseX: cx + Math.cos(angle) * rad,
          baseY: cy + Math.sin(angle) * rad,
          r: 3.5, type: "output", label: capLabels[i],
          orbitSpeed: 0.03 + i * 0.005,
          orbitRadius: rad,
          orbitPhase: angle,
          pulsePhase: i * 0.8,
          connections: [parentAgent],
        });
      }

      // Floating particles — ambient creative energy (15)
      for (let i = 0; i < 15; i++) {
        const angle = Math.random() * Math.PI * 2;
        const rad = 0.12 + Math.random() * 0.32;
        nodes.push({
          x: cx + Math.cos(angle) * rad,
          y: cy + Math.sin(angle) * rad,
          baseX: cx + Math.cos(angle) * rad,
          baseY: cy + Math.sin(angle) * rad,
          r: 1.2 + Math.random() * 1.5, type: "particle",
          label: "",
          orbitSpeed: 0.015 + Math.random() * 0.03,
          orbitRadius: rad,
          orbitPhase: angle,
          pulsePhase: Math.random() * 6,
          connections: [],
        });
      }

      // Cross-connections between inner agents
      for (let i = 1; i <= 6; i++) {
        nodes[i].connections.push(i === 6 ? 1 : i + 1); // ring
        if (i <= 3) nodes[i].connections.push(i + 3); // diagonals
      }

      return nodes;
    };

    if (!nodesRef.current) nodesRef.current = createNodes();

    // ── Spawn pulses (data flowing through network) ──
    const spawnPulse = (time) => {
      const nodes = nodesRef.current;
      // Pick a random connection
      const sourceIdx = 1 + Math.floor(Math.random() * 6); // from an agent
      const conns = nodes[sourceIdx].connections;
      const targetIdx = conns[Math.floor(Math.random() * conns.length)];
      pulsesRef.current.push({
        from: sourceIdx, to: targetIdx,
        progress: 0, speed: 0.008 + Math.random() * 0.012,
        born: time, size: 2 + Math.random() * 3,
      });
    };

    let lastPulseTime = 0;

    const draw = () => {
      if (!running) return;
      timeRef.current += 0.008;
      const time = timeRef.current;
      const sc = scrollRef.current;
      const isDark = darkRef.current;
      const nodes = nodesRef.current;

      // Theme-aware animation colors: white in dark mode, black in light mode
      const cR = isDark ? 255 : 0;
      const cG = isDark ? 255 : 0;
      const cB = isDark ? 255 : 0;

      // Spectrum color cycling for glows
      const spectrum = [
        [244, 63, 94],   // rose
        [236, 72, 153],  // pink
        [168, 85, 247],  // purple
        [99, 102, 241],  // indigo
        [59, 130, 246],  // blue
        [6, 182, 212],   // cyan
        [16, 185, 129],  // emerald
      ];
      const spectrumColor = (offset) => {
        const t2 = ((time * 0.4 + offset) % (spectrum.length)) ;
        const idx = Math.floor(t2);
        const frac = t2 - idx;
        const a = spectrum[idx % spectrum.length];
        const b = spectrum[(idx + 1) % spectrum.length];
        return [
          Math.round(a[0] + (b[0] - a[0]) * frac),
          Math.round(a[1] + (b[1] - a[1]) * frac),
          Math.round(a[2] + (b[2] - a[2]) * frac),
        ];
      };

      ctx.clearRect(0, 0, W, H);

      // ── Animate node positions ──
      const cx = W * 0.5;
      const cy = H * 0.5;
      const scale = Math.min(W, H);

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.type === "core") {
          // Gentle breathing
          n.x = n.baseX * W + Math.sin(time * 0.5) * 2;
          n.y = n.baseY * H + Math.cos(time * 0.4) * 2;
        } else {
          // Orbital drift
          const drift = time * n.orbitSpeed;
          const angle = n.orbitPhase + drift;
          const breathe = 1 + Math.sin(time * 0.3 + n.pulsePhase) * 0.04;
          const rad = n.orbitRadius * breathe;

          // Scroll influence — subtle shift
          const scrollShift = sc * 0.00008;

          n.x = cx + Math.cos(angle + scrollShift) * rad * scale;
          n.y = cy + Math.sin(angle + scrollShift * 0.7) * rad * scale;
        }
      }

      // Core position for reference
      const core = nodes[0];
      if (core.x < 1) { core.x *= W; core.y *= H; }

      // ── Draw connections ──
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        for (const ci of n.connections) {
          const target = nodes[ci];
          const dist = Math.hypot(n.x - target.x, n.y - target.y);
          const maxDist = scale * 0.45;
          if (dist > maxDist) continue;

          const alpha = (1 - dist / maxDist) * (n.type === "particle" ? 0.04 : 0.12);
          const pulse = 0.5 + 0.5 * Math.sin(time * 2 + i * 0.5);

          ctx.beginPath();
          // Curved connections via control point offset
          const mx = (n.x + target.x) / 2 + Math.sin(time + i) * 8;
          const my = (n.y + target.y) / 2 + Math.cos(time * 0.8 + i) * 8;
          ctx.moveTo(n.x, n.y);
          ctx.quadraticCurveTo(mx, my, target.x, target.y);

          ctx.strokeStyle = `rgba(${cR},${cG},${cB},${alpha * (0.6 + pulse * 0.4)})`;
          ctx.lineWidth = n.type === "output" ? 0.6 : 1;
          ctx.stroke();
        }
      }

      // ── Draw ambient field connections (proximity-based) ──
      const particles = nodes.filter(n => n.type === "particle");
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < scale * 0.15) {
            const alpha = (1 - dist / (scale * 0.15)) * 0.035;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${cR},${cG},${cB},${alpha})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        }
      }

      // ── Spawn + draw pulses ──
      if (time - lastPulseTime > 0.3 + Math.random() * 0.5) {
        spawnPulse(time);
        lastPulseTime = time;
      }

      const activePulses = [];
      for (const p of pulsesRef.current) {
        p.progress += p.speed;
        if (p.progress >= 1) continue;
        activePulses.push(p);

        const from = nodes[p.from];
        const to = nodes[p.to];
        const t = p.progress;
        const mx = (from.x + to.x) / 2 + Math.sin(p.born + p.from) * 8;
        const my = (from.y + to.y) / 2 + Math.cos(p.born * 0.8 + p.from) * 8;

        // Quadratic bezier point
        const px = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * mx + t * t * to.x;
        const py = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * my + t * t * to.y;

        const glow = 1 - Math.abs(t - 0.5) * 2; // brightest at midpoint
        ctx.beginPath();
        ctx.arc(px, py, p.size * (0.5 + glow * 0.5), 0, Math.PI * 2);
        const pc = spectrumColor(p.born * 2);
        ctx.fillStyle = `rgba(${pc[0]},${pc[1]},${pc[2]},${0.3 + glow * 0.5})`;
        ctx.fill();

        // Glow halo
        ctx.beginPath();
        ctx.arc(px, py, p.size * 2 + glow * 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${pc[0]},${pc[1]},${pc[2]},${0.04 + glow * 0.06})`;
        ctx.fill();
      }
      pulsesRef.current = activePulses;

      // ── Draw nodes ──
      for (let i = nodes.length - 1; i >= 0; i--) {
        const n = nodes[i];
        const pulse = 0.7 + 0.3 * Math.sin(time * 1.5 + n.pulsePhase);

        if (n.type === "core") {
          // Central hub — spectrum cycling glow
          const cc = spectrumColor(0);
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cc[0]},${cc[1]},${cc[2]},${0.06 * pulse})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cc[0]},${cc[1]},${cc[2]},${0.12 * pulse})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cc[0]},${cc[1]},${cc[2]},${0.85 * pulse})`;
          ctx.fill();


        } else if (n.type === "agent") {
          // Inner agents — ring with label
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.05 * pulse})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${cR},${cG},${cB},${0.6 * pulse})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // Filled dot center
          ctx.beginPath();
          ctx.arc(n.x, n.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.7 * pulse})`;
          ctx.fill();

          // Label
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.45 * pulse})`;
          ctx.font = `700 7px "JetBrains Mono", monospace`;
          ctx.textAlign = "center";
          ctx.fillText(n.label, n.x, n.y + n.r + 12);

        } else if (n.type === "output") {
          // Outer capability nodes — small filled circles
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.35 * pulse})`;
          ctx.fill();

          // Tiny label
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.22 * pulse})`;
          ctx.font = `500 5.5px "Helvetica Neue", sans-serif`;
          ctx.textAlign = "center";
          ctx.fillText(n.label, n.x, n.y + n.r + 9);

        } else {
          // Ambient particles — tiny dots
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r * pulse, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${cR},${cG},${cB},${0.12 * pulse})`;
          ctx.fill();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      running = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}

/* ─── Main ─── */
export default function App() {
  const scrollY = useParallax();
  const [dark, setDark] = useState(true);
  const [lang, setLang] = useState(() => navigator.language.startsWith("ja") ? "ja" : "en");
  const [email, setEmail] = useState("");
  const { toast, show: showToast } = useToast();
  const [headerSolid, setHeaderSolid] = useState(false);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const naturalRef = useRef(null);
  const [naturalProgress, setNaturalProgress] = useState(0);

  const t = themes[dark ? "dark" : "light"];
  const L = i18n[lang];

  useEffect(() => {
    setHeaderSolid(scrollY > 60);
    if (naturalRef.current) {
      const rect = naturalRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.8;
      const end = -rect.height * 0.3;
      const progress = Math.min(100, Math.max(0, ((start - rect.top) / (start - end)) * 100));
      setNaturalProgress(progress);
    }
  }, [scrollY]);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const helvetica = "'Helvetica Neue', 'Noto Sans JP', Helvetica, Arial, sans-serif";
  const mono = "'JetBrains Mono', 'SF Mono', monospace";

  return (
    <div style={{
      background: t.bg, color: t.text, fontFamily: helvetica,
      minHeight: "100vh", overflowX: "hidden",
      transition: "background 0.5s, color 0.5s",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Noto+Sans+JP:wght@300;400;500;700;900&display=swap" rel="stylesheet" />

      {/* ═══ HEADER ═══ */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: headerSolid ? t.headerSolid : t.headerBg,
        backdropFilter: "blur(12px)", transition: "background 0.3s",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto", padding: "0 40px", height: 64,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: "1.6rem", fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 900, letterSpacing: "-0.04em", color: t.text, transition: "color 0.5s" }}>−1</span>
          </div>

          <div style={{ display: "flex", gap: 0, alignItems: "center" }}>
            {L.navItems.map((s, i) => (
              <button key={s} onClick={() => go(["partners", "skills", "natural", "contact"][i])} style={{
                background: "none", border: "none", color: t.textSec, cursor: "pointer",
                fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: helvetica, padding: "8px 16px",
                transition: "color 0.15s",
              }}
                onMouseEnter={(e) => e.target.style.color = GOLD}
                onMouseLeave={(e) => e.target.style.color = t.textSec}
              >{s}</button>
            ))}

            <div style={{ width: 1, height: 20, background: t.border, margin: "0 8px" }} />

            <button onClick={() => setLang(lang === "en" ? "ja" : "en")} style={{
              background: "none", border: `1.5px solid ${t.border}`, color: t.textSec,
              cursor: "pointer", padding: "3px 10px", fontSize: "0.62rem",
              fontFamily: mono, fontWeight: 700, letterSpacing: "0.08em", transition: "all 0.15s", borderRadius: 8,
            }}
              onMouseEnter={(e) => { e.target.style.borderColor = GOLD; e.target.style.color = GOLD; }}
              onMouseLeave={(e) => { e.target.style.borderColor = t.border; e.target.style.color = t.textSec; }}
            >{lang === "en" ? "JP" : "EN"}</button>

            <button onClick={() => setDark(!dark)} aria-label="Toggle theme" style={{
              background: "none", border: "none", cursor: "pointer", padding: "4px 10px",
              fontSize: "0.95rem", color: dark ? "#ffffff" : GOLD, transition: "color 0.2s, transform 0.4s",
              transform: dark ? "rotate(0deg)" : "rotate(180deg)",
            }}
              onMouseEnter={(e) => e.target.style.opacity = "0.65"}
              onMouseLeave={(e) => e.target.style.opacity = "1"}
            >{dark ? "☽" : "☀"}</button>

            <div onClick={() => go("contact")} style={{
              background: GOLD_GRADIENT_H, padding: 2, borderRadius: 10, cursor: "pointer",
              transition: "opacity 0.15s", marginLeft: 8,
            }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              <div style={{
                background: t.bg, borderRadius: 8, padding: "8px 20px",
                fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: helvetica,
              }}>
                <GoldText>{L.navCta}</GoldText>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="hero-section" style={{
        minHeight: "100vh", padding: "220px 40px 80px", maxWidth: 1400,
        margin: "0 auto", position: "relative",
      }}>
        {/* Animation — absolutely positioned to the right, aligned with headline */}
        <div className="hero-animation" style={{
          position: "absolute", top: 200, right: 0, width: "50%", height: 500,
          pointerEvents: "none", zIndex: 1,
        }}>
          <ParametricRibbon scrollY={scrollY} dark={dark} />
        </div>

        <div style={{ position: "relative", zIndex: 2, maxWidth: "55%" }}>
          <Reveal>
            <p style={{
              fontSize: "0.65rem", fontFamily: mono, fontWeight: 700,
              color: GOLD, letterSpacing: "0.3em", textTransform: "uppercase",
              marginBottom: 32, display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ display: "inline-block", width: 40, height: 2, background: GOLD_GRADIENT_H }} />
              {L.badge}
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <h1 style={{
              fontSize: "clamp(3.2rem, 6.5vw, 7rem)", fontWeight: 900,
              lineHeight: 0.92, letterSpacing: "-0.035em", marginBottom: 36,
              color: t.text, transition: "color 0.5s",
            }}>
              {L.heroTitle1}<br />
              <GoldText style={{
                fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit",
                letterSpacing: "inherit", display: "inline",
              }}>{L.heroTitle2}</GoldText><br />
              {L.heroTitle3}
            </h1>
          </Reveal>

          <Reveal delay={0.16}>
            <p style={{
              fontSize: "1rem", color: t.textSec, maxWidth: 420,
              lineHeight: 1.65, marginBottom: 40, fontWeight: 400, transition: "color 0.5s",
            }}>{L.heroDesc}</p>
          </Reveal>

          <Reveal delay={0.24} style={{ display: "flex", gap: 16 }}>
            <div onClick={() => go("contact")} style={{
              background: GOLD_GRADIENT_H, padding: 2, borderRadius: 10, cursor: "pointer",
              transition: "opacity 0.2s",
            }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              <div style={{
                background: t.bg, borderRadius: 8, padding: "14px 36px",
                fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em",
                textTransform: "uppercase", fontFamily: helvetica,
              }}>
                <GoldText>{L.ctaPrimary}</GoldText>
              </div>
            </div>

            <button onClick={() => go("partners")} style={{
              background: "none", color: t.text, border: `2px solid ${t.border}`,
              padding: "14px 36px", fontSize: "0.72rem", fontWeight: 700,
              letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer",
              fontFamily: helvetica, transition: "all 0.2s", borderRadius: 8,
            }}
              onMouseEnter={(e) => { e.target.style.borderColor = GOLD; e.target.style.color = GOLD; }}
              onMouseLeave={(e) => { e.target.style.borderColor = t.border; e.target.style.color = t.text; }}
            >{L.ctaSecondary}</button>
          </Reveal>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section style={{
        borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`,
        background: t.bgAlt, transition: "all 0.5s",
      }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto", padding: "0 40px",
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        }}>
          {L.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{
                padding: "48px 0", textAlign: "center",
                borderRight: i < 3 ? `1px solid ${t.border}` : "none",
                transition: "border-color 0.5s",
              }}>
                {i === 0 ? (
                  <GoldText as="p" style={{
                    fontSize: "2.8rem", fontWeight: 900, letterSpacing: "-0.03em",
                    marginBottom: 6, fontFamily: helvetica, display: "block",
                  }}>{s.value}</GoldText>
                ) : (
                  <p style={{
                    fontSize: "2.8rem", fontWeight: 900, letterSpacing: "-0.03em",
                    color: t.text, marginBottom: 6, fontFamily: helvetica,
                    transition: "color 0.5s",
                  }}>{s.value}</p>
                )}
                <p style={{
                  fontSize: "0.68rem", fontWeight: 500, color: t.textMuted,
                  letterSpacing: "0.18em", textTransform: "uppercase", transition: "color 0.5s",
                }}>{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ PARTNERS ═══ */}
      {/* ═══ SHARING ═══ */}
      <section id="partners" style={{ padding: "100px 0", borderBottom: `1px solid ${t.border}` }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 40px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 80, alignItems: "start" }}>
            <Reveal>
              <div style={{ position: "sticky", top: 100 }}>
                <p style={{
                  fontSize: "0.62rem", fontFamily: mono, fontWeight: 700, color: GOLD,
                  letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12,
                }}>{L.partnersLabel}</p>
                <h2 style={{
                  fontSize: "3.5rem", fontWeight: 900, letterSpacing: "-0.04em",
                  color: t.text, lineHeight: 1, transition: "color 0.5s",
                }}>{L.partnersTitle}</h2>
                <div style={{ width: 48, height: 4, background: GOLD_GRADIENT_H, marginTop: 20 }} />
              </div>
            </Reveal>

            <div style={{
              display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1,
              background: t.border, transition: "background 0.5s",
            }}>
              {partnersList.map((name, i) => (
                <Reveal key={name} delay={i * 0.04}>
                  <div style={{
                    background: t.bg, padding: "36px 16px", textAlign: "center",
                    cursor: "default", transition: "all 0.2s", color: t.textSec,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, minHeight: 90,
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = t.textSec; }}
                  >
                    <span style={{ color: "inherit", transition: "color 0.15s", display: "flex" }}>{partnerIcons[name]}</span>
                    <span style={{
                      fontSize: "0.68rem", fontWeight: 700, color: "inherit",
                      letterSpacing: "0.12em", textTransform: "uppercase", transition: "color 0.15s",
                    }}>{name}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SKILLS ═══ */}
      <section id="skills" style={{ padding: "100px 0", borderBottom: `1px solid ${t.border}` }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 40px" }}>
          <Reveal>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 60 }}>
              <div>
                <p style={{
                  fontSize: "0.62rem", fontFamily: mono, fontWeight: 700, color: GOLD,
                  letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12,
                }}>{L.skillsLabel}</p>
                <h2 style={{
                  fontSize: "3.5rem", fontWeight: 900, letterSpacing: "-0.04em",
                  color: t.text, lineHeight: 1, transition: "color 0.5s",
                }}>{L.skillsTitle}</h2>
              </div>
              <div style={{ width: 48, height: 4, background: GOLD_GRADIENT_H }} />
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: t.border }}>
            {L.skills.map((s, i) => {
              const hovered = hoveredSkill === i;
              return (
                <Reveal key={s.num} delay={i * 0.06}>
                  <div
                    onMouseEnter={() => setHoveredSkill(i)}
                    onMouseLeave={() => setHoveredSkill(null)}
                    style={{
                      background: hovered ? "#0a0a0a" : t.bg,
                      padding: "44px 32px", minHeight: 220, cursor: "default",
                      transition: "background 0.25s", position: "relative",
                      display: "flex", flexDirection: "column", overflow: "hidden",
                    }}
                  >
                    {/* Gold top bar on hover */}
                    <div style={{
                      position: "absolute", top: 0, left: 0, height: 3,
                      width: hovered ? "100%" : "0%",
                      background: GOLD_GRADIENT_H,
                      transition: "width 0.4s ease",
                    }} />

                    {/* Watermark number */}
                    <span style={{
                      position: "absolute", top: -12, right: 12,
                      fontSize: "7rem", fontWeight: 900, lineHeight: 1,
                      fontFamily: helvetica, letterSpacing: "-0.05em",
                      pointerEvents: "none", transition: "opacity 0.25s, color 0.25s",
                      color: hovered ? GOLD_DARK : t.border,
                      opacity: hovered ? 0.25 : 0.5,
                    }}>{s.num}</span>

                    <p style={{
                      fontFamily: mono, fontSize: "0.6rem", fontWeight: 700,
                      color: GOLD, letterSpacing: "0.25em", marginBottom: 20,
                      position: "relative", transition: "color 0.25s",
                    }}>{s.num}</p>

                    <h3 style={{
                      fontSize: "1.15rem", fontWeight: 700, marginBottom: 12,
                      letterSpacing: "-0.01em", position: "relative",
                      color: hovered ? GOLD_LIGHT : t.text,
                      transition: "color 0.25s",
                    }}>{s.title}</h3>

                    <p style={{
                      fontSize: "0.82rem", lineHeight: 1.65, position: "relative",
                      color: hovered ? "#999" : t.textMuted,
                      transition: "color 0.25s", marginTop: "auto",
                    }}>{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ NATURAL ═══ */}
      <section id="natural" ref={naturalRef} style={{
        padding: "100px 0", background: t.bgAlt,
        borderBottom: `1px solid ${t.border}`,
        transition: "all 0.5s", position: "relative", overflow: "hidden",
      }}>
        {/* Huge decorative "05" watermark */}
        <div style={{
          position: "absolute", top: -40, right: 40,
          fontSize: "18rem", fontWeight: 900, lineHeight: 1,
          letterSpacing: "-0.05em", pointerEvents: "none", fontFamily: helvetica,
          color: GOLD_DARK, opacity: 0.05,
          transform: `translateY(${scrollY * 0.02}px)`,
        }}>05</div>

        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 40px", position: "relative", zIndex: 2 }}>
          <Reveal>
            <div style={{ marginBottom: 60 }}>
              <p style={{
                fontSize: "0.62rem", fontFamily: mono, fontWeight: 700, color: GOLD,
                letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12,
              }}>{L.naturalLabel}</p>
              <h2 style={{
                fontSize: "3.5rem", fontWeight: 900, letterSpacing: "-0.04em",
                color: t.text, lineHeight: 1, transition: "color 0.5s",
              }}>{L.naturalTitle}</h2>
            </div>
          </Reveal>

          {/* Timeline bar with gold fill */}
          <Reveal>
            <div style={{ position: "relative", marginBottom: 48 }}>
              <div style={{ width: "100%", height: 2, background: t.border }} />
              <div style={{
                width: `${naturalProgress}%`,
                height: 2, background: GOLD_GRADIENT_H,
                position: "absolute", top: 0, left: 0,
                transition: "width 0.1s linear",
              }} />
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 0 }}>
            {L.steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div style={{
                  paddingRight: 24, position: "relative",
                  borderLeft: i > 0 ? `1px solid ${t.border}` : "none",
                  paddingLeft: i > 0 ? 24 : 0,
                }}>
                  {/* Gold dot */}
                  <div style={{
                    width: 12, height: 12, borderRadius: "50%",
                    background: GOLD_GRADIENT, marginBottom: 24,
                    boxShadow: `0 0 0 4px ${t.bgAlt}, 0 0 0 6px ${t.border}, 0 0 12px ${GOLD_GLOW}`,
                  }} />

                  <GoldText as="p" style={{
                    fontFamily: mono, fontSize: "2rem", fontWeight: 900,
                    marginBottom: 8, letterSpacing: "-0.02em", display: "block",
                  }}>{s.num}</GoldText>

                  <p style={{
                    fontSize: "1rem", fontWeight: 700, color: t.text,
                    letterSpacing: "0.02em", marginBottom: 6, transition: "color 0.5s",
                  }}>{s.label}</p>

                  <p style={{
                    fontSize: "0.75rem", color: t.textMuted, fontWeight: 400,
                    transition: "color 0.5s",
                  }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INTEGRATIONS ═══ */}
      <section style={{ padding: "100px 0", borderBottom: `1px solid ${t.border}` }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 40px", textAlign: "center" }}>
          <Reveal>
            <p style={{
              fontSize: "0.62rem", fontFamily: mono, fontWeight: 700, color: GOLD,
              letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12,
            }}>{L.intLabel}</p>
            <h2 style={{
              fontSize: "2.8rem", fontWeight: 900, letterSpacing: "-0.03em",
              color: t.text, marginBottom: 48, transition: "color 0.5s",
            }}>{L.intTitle}</h2>
          </Reveal>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 0, justifyContent: "center" }}>
            {partnersList.map((name, i) => (
              <Reveal key={name} delay={i * 0.03}>
                <div style={{
                  border: `1.5px solid ${t.border}`, padding: "14px 28px",
                  fontSize: "0.68rem", color: t.textMuted, fontWeight: 700,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  cursor: "default", transition: "all 0.15s",
                  marginLeft: -1.5, marginTop: -1.5,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = t.textMuted; }}
                >
                  <span style={{ display: "flex", color: "inherit" }}>{partnerIcons[name]}</span>
                  {name}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CONTACT — gold split ═══ */}
      {/* ═══ BONDING ═══ */}
      <section id="contact" style={{ position: "relative", overflow: "hidden" }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto",
          display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: 500,
        }}>
          {/* Left — gold block */}
          <div style={{
            background: GOLD_GRADIENT, padding: "100px 60px",
            display: "flex", flexDirection: "column", justifyContent: "center",
            position: "relative",
          }}>
            <div style={{
              position: "absolute", top: 20, left: 20,
              fontSize: "0.55rem", fontFamily: mono, fontWeight: 700,
              color: "rgba(10,10,10,0.35)", letterSpacing: "0.25em",
            }}>{L.contactLabel.toUpperCase()}</div>

            <h2 style={{
              fontSize: "clamp(2.5rem, 5vw, 4.5rem)", fontWeight: 900,
              color: "#0a0a0a", letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 20,
            }}>{L.contactTitle}</h2>

            <p style={{ fontSize: "1rem", color: "rgba(10,10,10,0.55)", maxWidth: 360, lineHeight: 1.6 }}>
              {L.contactDesc}
            </p>

          </div>

          {/* Right — form */}
          <div style={{
            background: t.contactBg, padding: "100px 60px",
            display: "flex", flexDirection: "column", justifyContent: "center",
            transition: "background 0.5s",
          }}>
            <Reveal>
              <div>
                <label style={{
                  display: "block", fontSize: "0.62rem", fontFamily: mono,
                  fontWeight: 700, color: t.textMuted, letterSpacing: "0.25em",
                  textTransform: "uppercase", marginBottom: 12,
                }}>Enter Your Email</label>
                <form
                  action="https://formspree.io/f/xwvnkarp"
                  method="POST"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!email) return;
                    try {
                      const res = await fetch("https://formspree.io/f/xwvnkarp", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email }),
                      });
                      if (res.ok) { showToast("Thanks! We'll be in touch.", "success"); setEmail(""); }
                      else { showToast("Something went wrong. Please try again.", "error"); }
                    } catch { showToast("Something went wrong. Please try again.", "error"); }
                  }}
                  style={{ display: "flex", gap: 0 }}
                >
                  <input
                    type="email" name="email" placeholder="your@email.com" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    style={{
                      flex: 1, background: t.inputBg, border: `1.5px solid ${t.border}`,
                      borderRight: "none", padding: "16px 20px",
                      color: t.text, fontSize: "0.88rem", fontFamily: helvetica,
                      letterSpacing: "0.02em", outline: "none", transition: "all 0.5s",
                      borderRadius: "8px 0 0 8px",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#EC4899"}
                    onBlur={(e) => e.target.style.borderColor = t.border}
                  />
                  <div style={{
                    background: GOLD_GRADIENT_H, padding: 2, borderRadius: "0 10px 10px 0",
                    cursor: "pointer", transition: "opacity 0.2s",
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                  >
                    <button type="submit" style={{
                      background: t.contactBg, border: "none", borderRadius: "0 8px 8px 0",
                      padding: "16px 32px", fontWeight: 700, fontSize: "0.68rem",
                      letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
                      fontFamily: helvetica,
                    }}>
                      <GoldText>{L.submit}</GoldText>
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: `1px solid ${t.border}` }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto", padding: "28px 40px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ display: "flex", gap: 24 }}>
            {L.footerLinks.map((l) => (
              <a key={l} href="#" style={{
                color: t.textMuted, textDecoration: "none", fontSize: "0.68rem",
                fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase",
                transition: "color 0.15s",
              }}
                onMouseEnter={(e) => e.target.style.color = GOLD}
                onMouseLeave={(e) => e.target.style.color = t.textMuted}
              >{l}</a>
            ))}
          </div>
          <p style={{
            color: t.textMuted, fontSize: "0.65rem", letterSpacing: "0.08em",
            fontFamily: mono, transition: "color 0.5s",
          }}>{L.footerTag}</p>
        </div>
      </footer>

      <Toast toast={toast} theme={t} />

      <style>{`
        ::placeholder { color: ${t.textMuted} !important; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: ${GOLD}; color: #0a0a0a; }
        html { scroll-behavior: smooth; }
        @media (max-width: 1024px) {
          section > div { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 374px) {
          .hero-animation { display: none !important; }
        }
        @media (max-width: 768px) {
          header > div > div:last-child button:not(:last-child):not(:nth-last-child(2)):not(:nth-last-child(3)) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
